import React, { useState, useMemo } from 'react';
import useData from '../../hooks/useData';
import useToast from '../../hooks/useToast';
import { AddIcon, CloseIcon, CheckIcon } from '../ui/Icons';

const parseInlineFormatting = (line) => {
    if (!line) return '';
    const parts = line.split(/(\*\*.*?\*\*\*?)/g);
    return parts.filter(part => part).map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i} className="font-semibold text-white">{part.slice(2, -2)}</strong>;
        }
        return part;
    });
};

// --- INÍCIO DA ALTERAÇÃO 1: Adicionada a prop `titleColor` ---
const Checklist = ({ task, title, field, placeholder, titleColor = 'text-gray-300' }) => {
// --- FIM DA ALTERAÇÃO 1 ---
    const { handleUpdateTask } = useData();
    const { showToast } = useToast();
    const [newItemText, setNewItemText] = useState('');
    const [activeTab, setActiveTab] = useState('pending');

    const items = task[field] || [];
    
    const { pendingItems, completedItems } = useMemo(() => {
        const pending = items.filter(item => !item.completed);
        const completed = items.filter(item => item.completed);
        return { pendingItems: pending, completedItems: completed };
    }, [items]);

    const progress = items.length > 0 ? (completedItems.length / items.length) * 100 : 0;

    const handleItemsUpdate = (updatedItems) => {
        handleUpdateTask(task.id, { [field]: updatedItems });
    };

    const handleAddItem = () => {
        if (newItemText.trim() === '') return;
        const newItem = { id: Date.now(), text: newItemText, completed: false };
        handleItemsUpdate([...items, newItem]);
        setNewItemText('');
        showToast("Item adicionado ao checklist!");
    };

    const handleToggleItem = (itemId) => {
        const updated = items.map(item => item.id === itemId ? { ...item, completed: !item.completed } : item);
        handleItemsUpdate(updated);
    };

    const handleDeleteItem = (itemId) => {
        const updated = items.filter(item => item.id !== itemId);
        handleItemsUpdate(updated);
        showToast("Item removido do checklist.", "error");
    };

    const renderTaskList = (tasksToRender) => (
        tasksToRender.map(item => (
            <div key={item.id} className="flex items-center justify-between text-sm group rounded-lg hover:bg-white/5">
                <label className="flex items-start gap-3 cursor-pointer p-2 flex-1" onClick={(e) => { e.preventDefault(); handleToggleItem(item.id); }}>
                    <div className={`mt-1 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${item.completed ? 'bg-indigo-500 border-indigo-400' : 'border-gray-500 group-hover:border-gray-400'}`}>
                        {item.completed && <CheckIcon size={14} className="text-white" />}
                    </div>
                    <span className={`flex-1 transition-colors duration-200 whitespace-pre-wrap ${item.completed ? 'line-through text-gray-500' : 'text-gray-200'}`}>
                        {parseInlineFormatting(item.text)}
                    </span>
                </label>
                <button onClick={() => handleDeleteItem(item.id)} className="text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-2 ml-2">
                    <CloseIcon size={16} />
                </button>
            </div>
        ))
    );

    return (
        <div className="bg-white/5 rounded-lg p-4">
            {/* --- INÍCIO DA ALTERAÇÃO 2: Aplicando a cor recebida pela prop `titleColor` --- */}
            <h3 className={`text-base font-semibold mb-2 ${titleColor}`}>{title} ({completedItems.length}/{items.length})</h3>
            {/* --- FIM DA ALTERAÇÃO 2 --- */}
            
            {items.length > 0 && (
                <div className="w-full bg-black/20 rounded-full h-2 mb-4">
                    <div className="bg-indigo-500 h-2 rounded-full transition-all duration-500" style={{ width: `${progress}%` }}></div>
                </div>
            )}
            
            <div className="flex gap-1 rounded-lg bg-black/20 p-1 mb-3">
                <button onClick={() => setActiveTab('pending')} className={`flex-1 py-1 text-xs rounded-md transition-colors ${activeTab === 'pending' ? 'bg-white/10' : 'hover:bg-white/5'}`}>
                    A Fazer ({pendingItems.length})
                </button>
                <button onClick={() => setActiveTab('completed')} className={`flex-1 py-1 text-xs rounded-md transition-colors ${activeTab === 'completed' ? 'bg-white/10' : 'hover:bg-white/5'}`}>
                    Finalizados ({completedItems.length})
                </button>
            </div>

            <div className="space-y-1 max-h-48 overflow-y-auto pr-2">
                {activeTab === 'pending' ? renderTaskList(pendingItems) : renderTaskList(completedItems)}
            </div>

            <div className="flex space-x-2 mt-4">
                <input type="text" value={newItemText} onChange={(e) => setNewItemText(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleAddItem()} placeholder={placeholder} className="flex-1 p-2 bg-black/20 rounded-lg border border-transparent focus:border-indigo-500 focus:outline-none text-sm" />
                <button onClick={handleAddItem} className="p-2 bg-indigo-500 rounded-lg hover:bg-indigo-400 transition-colors">
                    <AddIcon size={20} />
                </button>
            </div>
        </div>
    );
};

export default Checklist;